import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";
import { COMPANY, buildWhatsAppLink, telLink } from "../../lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2075&auto=format&fit=crop";

export default function Hero({ onOpenLead }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 150]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Parallax bg image */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 z-0 parallax-bg"
      >
        <img
          src={HERO_IMG}
          alt="Dholera Smart City luxury residence"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-24 min-h-screen flex flex-col justify-end">
        <motion.div style={{ opacity }} className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="divider-gold" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#D4AF37] font-medium">
              {COMPANY.tagline}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] text-white font-light tracking-tight"
          >
            Invest in the{" "}
            <span className="gold-gradient-text italic">Future</span> of
            <br />
            Dholera Smart City.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-8 text-lg md:text-xl text-white/70 font-light max-w-2xl leading-relaxed"
          >
            Unlock exceptional investment opportunities in India&apos;s first
            greenfield smart city &mdash; guided by a decade of trusted advisory and
            curated, verified land portfolios.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-site-visit-btn"
              onClick={onOpenLead}
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium tracking-[0.2em] uppercase text-xs px-8 py-4 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_45px_rgba(212,175,55,0.55)]"
            >
              Book Free Site Visit
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 px-6 py-4 border border-[#25D366]/60 text-[#25D366] text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#25D366]/10 transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
              WhatsApp Now
            </a>
            <a
              href={telLink(COMPANY.phones[1])}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 px-6 py-4 border border-white/20 text-white text-xs tracking-[0.2em] uppercase font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              <Phone className="w-4 h-4" strokeWidth={1.6} />
              Call Now
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-20 grid grid-cols-3 gap-px bg-white/5 border border-white/5"
          >
            {[
              { value: COMPANY.stats.clients, label: "Happy Clients" },
              { value: COMPANY.stats.acres, label: "Acres Facilitated" },
              { value: COMPANY.stats.years, label: "Years Experience" },
            ].map((s, i) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${i}`}
                className="bg-black/60 backdrop-blur-xl px-6 py-8 text-center"
              >
                <div className="font-serif text-4xl md:text-5xl text-[#D4AF37] font-light">
                  {s.value}
                </div>
                <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-white/60">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 right-6 md:right-10 hidden md:flex flex-col items-center gap-3 text-white/40">
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center mt-12">
          Scroll
        </span>
        <div className="w-px h-16 bg-gradient-to-b from-[#D4AF37] to-transparent" />
      </div>
    </section>
  );
}
